import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { authSelector } from "../components/Auth/AuthSlice";
import { profileSelector } from "../components/Profile/ProfileSlice";
import {
  canAccessAbac,
  canAccessAbacAny,
  canAccessAbacView,
  canAccessDefaultSetting,
  canAccessNavbarItem,
  canAccessNavbarPermission,
  canAccessWalletBalance,
  canAccessWalletDownload,
  canAccessWalletPage,
  canAccessWalletPricing,
  canAccessWalletPricingDownload,
  canAccessPlatformDoc,
  canAccessPlatformDocumentsPage,
  canAccessFeatureDocument,
  canAccessPlatformDocApiCard,
  canAccessPlatformDocFeature,
  canViewPlatformDocumentsUi,
  isMultiAccessUser,
  shouldEnforceAbacForUser,
} from "../components/User/Abac/abacAccess";


export function useAbacSessionPending() {
  const { user, abacPermissions, abacSessionLoaded, isFetchingAbacSession } =
    useSelector(authSelector);
  if (!shouldEnforceAbacForUser(user, globalThis.profileData)) return false;
  // Keep shell skeleton up through fetch so an empty sidebar never flashes.
  if (isFetchingAbacSession) return true;
  return abacPermissions == null && !abacSessionLoaded;
}


export default function useAbacAccess() {
  const {
    user,
    abacPermissions,
    abacSessionLoaded,
    abacSessionOk,
    abacAccessKey,
    isFetchingAbacSession,
  } = useSelector(authSelector);
  const profileState = useSelector(profileSelector) || {};
  const profileData = profileState?.getProfile || globalThis.profileData || {};

  const isMultiAccess = useMemo(
    () => isMultiAccessUser(user, profileData),
    [user, profileData]
  );
  const shouldEnforce = useMemo(
    () => shouldEnforceAbacForUser(user, profileData),
    [user, profileData]
  );

  const canAccess = useCallback(
    (...pathOrSegments) => {
      const path =
        pathOrSegments.length === 1 && Array.isArray(pathOrSegments[0])
          ? pathOrSegments[0]
          : pathOrSegments;
      return canAccessAbac(abacPermissions, path, user, profileData);
    },
    [abacPermissions, user, profileData]
  );

  const canAccessAny = useCallback(
    (paths) => canAccessAbacAny(abacPermissions, paths, user, profileData),
    [abacPermissions, user, profileData]
  );

  const canAccessView = useCallback(
    (...pathOrSegments) => {
      const path =
        pathOrSegments.length === 1 && Array.isArray(pathOrSegments[0])
          ? pathOrSegments[0]
          : pathOrSegments;
      return canAccessAbacView(abacPermissions, path, user, profileData);
    },
    [abacPermissions, user, profileData]
  );

  return {
    user,
    profileData,
    abacPermissions,
    abacAccessKey,
    abacSessionLoaded: Boolean(abacSessionLoaded),
    abacSessionOk: Boolean(abacSessionOk),
    isFetchingAbacSession: Boolean(isFetchingAbacSession),
    isMultiAccess,
    shouldEnforce,
    canAccess,
    canAccessAny,
    canAccessView,
    canAccessNavbar: (key) =>
      canAccessNavbarItem(abacPermissions, key, user, profileData),
    canAccessNavbarAction: (itemKey, actionKey) =>
      canAccessNavbarPermission(
        abacPermissions,
        itemKey,
        actionKey,
        user,
        profileData
      ),
    canAccessWalletUi: canAccessWalletBalance(
      abacPermissions,
      user,
      profileData
    ),
    canAccessWalletPage: canAccessWalletPage(
      abacPermissions,
      user,
      profileData
    ),
    canAccessWalletDownload: canAccessWalletDownload(
      abacPermissions,
      user,
      profileData
    ),
    canAccessWalletPricing: canAccessWalletPricing(
      abacPermissions,
      user,
      profileData
    ),
    canAccessWalletPricingDownload: canAccessWalletPricingDownload(
      abacPermissions,
      user,
      profileData
    ),
    canAccessDefault: (key) =>
      canAccessDefaultSetting(abacPermissions, key, user, profileData),
    canAccessPlatformDocumentsPage: canAccessPlatformDocumentsPage(
      abacPermissions,
      user,
      profileData
    ),
    canAccessPlatformDoc: (docKey, action = "view") =>
      canAccessPlatformDoc(
        abacPermissions,
        docKey,
        action,
        user,
        profileData
      ),
    canAccessFeatureDocument: (fileName) =>
      canAccessFeatureDocument(
        abacPermissions,
        fileName,
        user,
        profileData
      ),
    canAccessFeatureDoc: (key) =>
      canAccessPlatformDocFeature(abacPermissions, key, user, profileData),
    canAccessApiDoc: (cardKey) =>
      canAccessPlatformDocApiCard(abacPermissions, cardKey, user, profileData),
    canViewPlatformDocuments: canViewPlatformDocumentsUi(
      abacPermissions,
      user,
      profileData
    ),
  };
}
